import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CurrentView } from "@/hooks/use-current-view";
import { useGenerateReport } from "@/hooks/use-generate-report";
import { useAppState } from "@/store/useApp";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

interface GenerateReportTriggerProps {
  currentView: CurrentView;
}

export const GenerateReportTrigger = ({
  currentView,
}: GenerateReportTriggerProps) => {
  const [warningShown, setWarningShown] = useState(false);
  const { generateReport, isGeneratingReport, isEnabled } = useGenerateReport();
  const currentDocument = useAppState((state) => state.currentDocument);
  const isPublishingFeedback = useAppState((state) => state.isPublishingFeedback);
  const navigate = useNavigate();
  const pendingSuggestions =
    currentDocument?.suggestions.filter(
      (suggestion) =>
        suggestion.status === "pending" && suggestion.rubricCriteriaId
    ).length ?? 0;
  const grading = useAppState((state) => state.grading);
  const isPublished = grading?.feedback?.status === "published" || false;
  const shouldShowWarning =
    pendingSuggestions > 0 &&
    !warningShown &&
    currentView === "reader" &&
    !isPublished;

  console.log("pendingSuggestions", pendingSuggestions);

  useEffect(() => {
    setWarningShown(false);
  }, [currentDocument?.id]);

  const handleGradingClick = async () => {
    if (!currentDocument || isGeneratingReport) return;
    await generateReport(currentDocument.id);
  };

  const handleReview = () => {
    navigate(`/documents/${currentDocument?.id}/rubric`);
  };

  const generateButton = (
    <Button
      id="hint-generate-reports"
      onClick={shouldShowWarning ? () => {} : handleGradingClick}
      disabled={!isEnabled || isGeneratingReport || isPublishingFeedback}
    >
      {isPublished ? "View report" : "Generate report"}
      {isGeneratingReport && <Loader2 className="h-4 w-4 animate-spin" />}
    </Button>
  );

  if (shouldShowWarning === false) {
    // No warning, show generate button
    return <>{generateButton}</>;
  }

  const suggestionText =
    pendingSuggestions === 1 ? "AI suggestion" : "AI suggestions";

  return (
    <Popover>
      <PopoverTrigger asChild>{generateButton}</PopoverTrigger>
      <PopoverContent>
        <div className="grid gap-4">
          <div className="space-y-4">
            <h4>Pending AI suggestions</h4>
            <p className="text-sm text-muted-foreground">
              We found{" "}
              <span className="font-semibold">
                {pendingSuggestions} {suggestionText}
              </span>{" "}
              that you haven't approved or rejected yet.
            </p>
          </div>
          <div className="flex gap-2">
            <PopoverClose asChild>
              <Button onClick={handleGradingClick} variant="outline">
                Ignore and continue
              </Button>
            </PopoverClose>
            <PopoverClose asChild>
              <Button onClick={handleReview}>Review</Button>
            </PopoverClose>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
